import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Alert,
    CircularProgress,
    Box,
    Typography,
    Chip,
    IconButton
} from '@mui/material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CloseIcon from '@mui/icons-material/Close';
import ShieldIcon from '@mui/icons-material/Shield';
import { useSelector } from 'react-redux';
import { ReservationService } from '../api/services/reservationService';

export default function ApproveGovernmentModal({
    open,
    onClose,
    reservation,
    tenantId,
    onSuccess,
    setNotification
}) {
    const { user } = useSelector((state) => state.auth || {});

    const [approvalReference, setApprovalReference] = useState('');
    const [notes, setNotes] = useState('');
    const [touched, setTouched] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState(null);

    // MANAGE_CHECKIN permission verification
    const hasManageCheckin = useMemo(() => {
        if (!user) return true; // fallback if user is not loaded
        const roleName = user.role?.name || user.roleName || '';
        if (roleName === 'Super Admin' || roleName === 'Admin') return true;

        let rawPerms = [];
        if (Array.isArray(user.customPermissions) && user.customPermissions.length > 0) {
            rawPerms = user.customPermissions;
        } else if (Array.isArray(user.permissions) && user.permissions.length > 0) {
            rawPerms = user.permissions;
        } else if (user.role && Array.isArray(user.role.permissions)) {
            rawPerms = user.role.permissions;
        }

        const perms = rawPerms.map(p => {
            if (typeof p === 'string') return p;
            if (p && typeof p === 'object') return p.key || p.name || p.code || '';
            return '';
        }).filter(Boolean);

        return perms.includes('*') || perms.includes('MANAGE_CHECKIN') || perms.includes('CAN_MANAGE_CHECKIN');
    }, [user]);

    useEffect(() => {
        if (open) {
            setApprovalReference('');
            setNotes('');
            setError(null);
            setTouched(false);
        }
    }, [open]);

    const handleApprove = async () => {
        setTouched(true);
        if (!approvalReference.trim()) {
            setError('Approval Reference is required.');
            return;
        }
        if (!notes.trim()) {
            setError('Clearance Notes are required.');
            return;
        }

        const id = reservation?.groupId || reservation?.id || reservation?._id;
        if (!id) {
            setError('Reservation identifier missing.');
            return;
        }

        setIsProcessing(true);
        setError(null);

        try {
            const payload = {
                approvalReference: approvalReference.trim(),
                notes: notes.trim()
            };

            await ReservationService.approveGovernmentClearance(id, tenantId, payload);

            const successMsg = `Government clearance approved successfully (Ref: ${approvalReference.trim()}). Reservation is now Confirmed.`;
            if (setNotification) {
                setNotification({
                    open: true,
                    message: successMsg,
                    severity: 'success'
                });
            }

            if (onSuccess) {
                onSuccess(id);
            }
            onClose();
        } catch (err) {
            console.error('Failed to approve government status:', err);
            const status = err?.response?.status;
            const errMsg = status === 403
                ? "Permission denied: You do not have the required 'MANAGE_CHECKIN' permission to approve government status."
                : (err?.response?.data?.message || err?.message || 'Failed to approve government status.');

            if (setNotification) {
                setNotification({
                    open: true,
                    message: errMsg,
                    severity: 'error'
                });
            } else {
                setError(errMsg);
            }
        } finally {
            setIsProcessing(false);
        }
    };

    if (!reservation) return null;

    const bookingId = reservation?.bookingId || reservation?.id || reservation?._id || 'N/A';
    const guestName = reservation?.guestName || reservation?.guest?.name || reservation?.guest?.fullName || 'N/A';

    return (
        <Dialog
            open={open}
            onClose={isProcessing ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3.5,
                    p: 1,
                    boxShadow: '0 20px 48px rgba(15, 23, 42, 0.16)'
                }
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2.5,
                        bgcolor: '#ecfdf5',
                        color: '#059669',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #a7f3d0'
                    }}>
                        <VerifiedUserIcon sx={{ fontSize: 22 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" fontWeight="800" sx={{ fontSize: '1.1rem', color: '#0f172a', letterSpacing: '-0.01em' }}>
                            Approve Government Status
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.78rem' }}>
                            Dubai Economy & Tourism (DET) / Dubai Police Clearance
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small" disabled={isProcessing} sx={{ color: '#94a3b8' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ py: 2.5 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
                        {error}
                    </Alert>
                )}

                {!hasManageCheckin && (
                    <Alert severity="warning" icon={<ShieldIcon fontSize="inherit" />} sx={{ mb: 2.5, borderRadius: 2 }}>
                        <b>Permission Notice:</b> You do not currently have the <code>MANAGE_CHECKIN</code> permission. Submitting this request may be rejected by the server authorization rules.
                    </Alert>
                )}

                {/* Booking Summary Card */}
                <Box sx={{
                    p: 2,
                    mb: 3,
                    bgcolor: '#f8fafc',
                    borderRadius: 2.5,
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1.5
                }}>
                    <Box>
                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                            Booking ID
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: '#0f172a', fontFamily: 'monospace' }}>
                            {bookingId}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                            Guest Name
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                            {guestName}
                        </Typography>
                    </Box>
                    <Chip
                        label="Government Status: Pending"
                        size="small"
                        sx={{
                            bgcolor: '#fef3c7',
                            color: '#92400e',
                            fontWeight: 700,
                            fontSize: '0.72rem',
                            border: '1px solid #fde68a'
                        }}
                    />
                </Box>

                {/* Form Fields */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <TextField
                        fullWidth
                        label="Approval Reference"
                        value={approvalReference}
                        onChange={(e) => {
                            setApprovalReference(e.target.value);
                            if (error) setError(null);
                        }}
                        placeholder="e.g. DET-2026-98124"
                        required
                        disabled={isProcessing}
                        error={touched && !approvalReference.trim()}
                        helperText={touched && !approvalReference.trim() ? "Approval reference is required" : "Enter the official approval reference identifier from the Dubai Police or DET portal."}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2
                            }
                        }}
                    />

                    <TextField
                        fullWidth
                        label="Clearance Notes"
                        value={notes}
                        onChange={(e) => {
                            setNotes(e.target.value);
                            if (error) setError(null);
                        }}
                        placeholder="e.g. Verified and cleared with Dubai Police / DET portal"
                        multiline
                        rows={3}
                        required
                        disabled={isProcessing}
                        error={touched && !notes.trim()}
                        helperText={touched && !notes.trim() ? "Clearance notes are required" : "Enter verification audit log notes for local statutory compliance."}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2
                            }
                        }}
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    onClick={onClose}
                    disabled={isProcessing}
                    sx={{ textTransform: 'none', fontWeight: 600, color: '#64748b' }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleApprove}
                    disabled={isProcessing || !approvalReference.trim() || !notes.trim()}
                    startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : <VerifiedUserIcon sx={{ fontSize: 18 }} />}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        px: 3,
                        py: 0.85,
                        borderRadius: 2,
                        bgcolor: '#4146e0',
                        '&:hover': {
                            bgcolor: '#3539bc'
                        },
                        boxShadow: '0 2px 8px rgba(65, 70, 224, 0.25)'
                    }}
                >
                    {isProcessing ? 'Approving...' : 'Approve Government Status'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
